import colors from '@constants/colors';
import { css, keyframes } from '@emotion/react';
import styled from '@emotion/styled';
import RefreshIcon from '@icons/Refresh';
import { BottomSheet } from '@stackflow/plugin-basic-ui';
import { ActivityComponentType } from '@stackflow/react';
import { pressActionEffect } from '@styles/mixin';
import { useTranslation } from 'react-i18next';

import OrderSheetLayout from '../common/OrderSheetLayout';
import PrepaidCardRow from './components/PrepaidCardRow';
import PrepaidCardRowSkeletons from './components/PrepaidCardRowSkeletons';
import useOrderPrepaidCards from './hooks/useOrderPrepaidCards';

declare module '@stackflow/config' {
    interface Register {
        OrderPrepaidCardBottomSheet: {};
    }
}

/** 결제에 사용할 선불카드 선택 */
const OrderPrepaidCardBottomSheet: ActivityComponentType<'OrderPrepaidCardBottomSheet'> = () => {
    const { t } = useTranslation('order');
    const {
        cards, selectedCardId, applyDisabled,
        togglePrepaidCard, handleApply, handleClose, handleRefresh,
        isLoading,
    } = useOrderPrepaidCards();

    return (
        <BottomSheet>
            <OrderSheetLayout title={t('prepaidSheet.title')}
                titleTrailing={(
                    <RefreshButton type='button'
                        aria-label='refresh'
                        onClick={handleRefresh}
                        disabled={isLoading}
                        css={pressActionEffect}>
                        <RefreshIcon size={24}
                            color={colors.gray9}
                            css={isLoading && spinningCss} />
                    </RefreshButton>
                )}
                isEmpty={!isLoading && cards?.length === 0}
                emptyText={t('prepaidSheet.empty')}
                closeLabel={t('prepaidSheet.close')}
                onClose={handleClose}
                applyLabel={t('prepaidSheet.apply')}
                onApply={handleApply}
                applyDisabled={applyDisabled}>
                {isLoading ? (
                    <PrepaidCardRowSkeletons />
                ) : cards?.map((card) => (
                    <PrepaidCardRow key={card.id}
                        card={card}
                        isSelected={selectedCardId === card.id}
                        onToggle={() => togglePrepaidCard(card.id)} />
                ))}
            </OrderSheetLayout>
        </BottomSheet>
    );
};

const RefreshButton = styled.button`
    align-items: center;
    background: transparent;
    display: flex;
    justify-content: center;
    padding: 0;

    &:disabled {
        opacity: 1;
    }
`;

const spin = keyframes`
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
`;

const spinningCss = css`
    animation: ${spin} 800ms linear infinite;
`;

export default OrderPrepaidCardBottomSheet;
